<template>
  <b-modal
    id="modal-photo-action"
    :title="modalTitle"
    size="lg"
    @hidden="onHidden"
    @shown="onModalShown"
  >
    <div class="modal-body">
      <b-alert
        variant="warning"
        v-if="!this.$store.getters.reportUuid && this.$store.getters.selectedCount > 1 && this.$store.getters.selectedCount <= 100"
        :show="true"
      >
        {{ $t('modal.address.multipleReports', { count: this.$store.getters.selectedCount }) }}
      </b-alert>
      <!-- If no image is uploaded, show the dropzone -->
      <div
        v-if="!uploadedPhotoKey"
        class="dropzone"
        :class="{ 'active-dropzone': active }"
        @dragenter.prevent="toggleActive"
        @dragleave.prevent="toggleActive"
        @dragover.prevent
        @drop.prevent="toggleActive"
        @drop="handleDrop"
      >
        <label for="modalDropzoneFile">
          <i class="el-icon-upload"></i>
          <span>
            {{ $t('dropzone.drop') }}
            <span class="dropzone-blue">{{ $t('dropzone.click') }}</span>
          </span>
        </label>
        <input
          type="file"
          ref="fileInput"
          id="modalDropzoneFile"
          @change="handleFileSelect"
        />
      </div>

      <!-- PREVIEW if we have a photo key -->
      <div
        v-else
        class="text-center"
      >
        <b-img
          :src="previewUrl"
          alt="Uploaded photo"
          fluid
          class="mb-2"
        />
        <b-button
          v-if="!photoSaved || !uploadedPhotoKey"
          variant="danger"
          size="sm"
          @click="deleteUploadedPhoto"
        >
          {{ $t('action.delete') }}
        </b-button>
      </div>

      <!-- Once an image is uploaded, show the preview and a delete button -->
      <!-- <div v-else>
        <b-row
          class="mb-2"
          align-h="center"
        >
          <b-col cols="auto">
            <b-img
              :src="previewUrl"
              alt="Uploaded photo"
              fluid
              fluid-grow
              class="mb-2"
            ></b-img>
          </b-col>
        </b-row>
        <b-row align-h="center">
          <b-col cols="auto">
            <b-button
              variant="danger"
              size="sm"
              @click="deleteUploadedPhoto"
            >
              {{ $t('action.delete') }}
            </b-button>
          </b-col>
        </b-row>
      </div> -->

      <!-- Optional progress bar during upload -->
      <b-progress
        v-if="progressBlock"
        :value="uploadProgress"
        max="100"
        class="mt-2"
      ></b-progress>
    </div>

    <template #modal-footer="{ hide }">
      <b-button
        variant="light"
        size="sm"
        @click="hide"
      >
        {{ $t('action.cancel') }}
      </b-button>
      <b-button
        v-if="!photoSaved || !uploadedPhotoKey"
        variant="primary"
        size="sm"
        @click="submitPhoto"
      >
        {{ $t('action.save') }}
      </b-button>
      <b-button
        v-else
        variant="danger"
        size="sm"
        @click="deletePhotoAndRecord"
      >
        {{ $t('action.delete') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axiosResource from '@/utils/axiosResource';
import { globalSwal } from '@/plugins/globalSwal';

export default {
  name: 'ModalPhotoAction',
  props: {
    // Expecting "closure" or "solved"
    photoType: {
      type: String,
      required: false, // Not required anymore
      default: 'check', // Defaults to an empty string
    },
  },
  data() {
    return {
      modalTitle: '',
      active: false,
      selectedFile: null,
      previewUrl: '',
      uploadedPhotoKey: '',
      progressBlock: false,
      uploadProgress: 0,
      photoSaved: false, // Flag to determine if the user saved the photo
    };
  },
  computed: {
    // Instead of using the prop, use Vuex getter
    currentPhotoType() {
      return this.$store.getters['photoType'];
    },
    modalTitleComputed() {
      // Update the title based on currentPhotoType
      return this.currentPhotoType === 'closure'
        ? this.$t('modal.photo.closureTitle')
        : this.$t('modal.photo.solvedTitle');
    },
    existingKey() {
      return this.$store.getters['existingPhotoKey'];
    },
    existingUrl() {
      return this.$store.getters['existingPhotoUrl'];
    },
  },
  methods: {
    onModalShown() {
      // Update the title from Vuex value
      this.modalTitle = this.modalTitleComputed;

      // Pre‑populate with existing key/URL
      this.uploadedPhotoKey = this.existingKey;
      if (this.existingUrl) {
        this.previewUrl = this.existingUrl;
      }

      // If there’s already a photo on record, mark as “saved”
      this.photoSaved = !!this.uploadedPhotoKey;

      console.log(
        `onModalShown previewUrl= ${this.previewUrl}, existingKey= ${this.existingKey}, photoSaved=${this.photoSaved}, this.$store.getters['photoType']=${this.$store.getters['photoType']}`
      );
    },
    toggleActive() {
      this.active = !this.active;
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        // Create a temporary preview URL
        this.previewUrl = URL.createObjectURL(file);
        // Start the upload immediately
        this.uploadFile();
      }
    },
    handleDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.selectedFile = file;
        this.previewUrl = URL.createObjectURL(file);
        this.uploadFile();
      }
    },
    async uploadFile() {
      this.progressBlock = true;
      const formData = new FormData();
      formData.append('image', this.selectedFile);

      try {
        // Upload the image (this endpoint applies your S3 upload logic)
        const response = await axiosResource.post('/uploads/image', formData, {
          onUploadProgress: (uploadEvent) => {
            this.uploadProgress = Math.round(
              (uploadEvent.loaded / uploadEvent.total) * 100
            );
          },
        });
        // Response should contain the S3 key and a presigned URL for preview.
        this.uploadedPhotoKey = response.data.key;
        this.previewUrl = response.data.presignedUrl;
        this.progressBlock = false;
        this.uploadProgress = 0;
      } catch (error) {
        console.error('Error uploading file:', error);
        globalSwal.errorSimple(this.$t('error.photoUpload'));
        this.progressBlock = false;
        this.uploadProgress = 0;
      }
    },
    async deleteUploadedPhoto() {
      if (this.uploadedPhotoKey) {
        try {
          await axiosResource.delete(
            `/uploads/deleteImage/${this.uploadedPhotoKey}`
          );
          globalSwal.successSimple(this.$t('success.photoDeleted'));
        } catch (error) {
          console.error('Error deleting photo:', error);
          globalSwal.errorSimple(this.$t('error.photoDelete'));
        }
      }
      // Reset the component's state so the dropzone reappears.
      this.uploadedPhotoKey = '';
      this.selectedFile = null;
      this.previewUrl = '';
      // Use the ref to reset the input value if it exists
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    async submitPhoto() {
      if (!this.uploadedPhotoKey) {
        globalSwal.errorSimple(this.$t('error.noFileSelected'));
        return;
      }
      try {
        // Determine which reportUuid to use.
        // If a single report is provided via props, use that; otherwise, get it from Vuex.
        const reportUuid =
          this.report && this.report.uuid
            ? this.report.uuid
            : this.$store.getters.reportUuid;

        // Use a POST request to the new endpoint.
        // Here we use this.currentPhotoType if you’re using Vuex for photo type,
        // or use this.photoType if you're relying on the prop.
        // For example, if you use Vuex:
        const photoType = this.currentPhotoType || this.photoType;
        console.log(photoType);

        const response = await axiosResource.post(
          `reports/document/photo/${photoType}`,
          { reportUuid, key: this.uploadedPhotoKey }
        );

        if (response.data.success) {
          globalSwal.successSimple(this.$t('success.photoUpdated'));
          this.photoSaved = true;
          // this.$emit('photo-updated', this.uploadedPhotoKey);

          this.$emit(
            'photo-updated',
            this.uploadedPhotoKey,
            this.previewUrl,
            response.data.affectedUuids,
            photoType
          );
          this.$bvModal.hide('modal-photo-action');
        } else {
          globalSwal.errorSimple(this.$t('error.photoUpdate'));
        }
      } catch (error) {
        console.error('Error submitting photo:', error);
        globalSwal.errorSimple(this.$t('error.photoUpdate'));
      }
    },
    async deletePhotoAndRecord() {
      const photoType = this.$store.getters['photoType']; // "closure" or "solved"
      const reportUuid =
        this.report && this.report.uuid
          ? this.report.uuid
          : this.$store.getters['reportUuid'];

      try {
        // 1) Ask the API to clear the DB field (key = null)
        //    and get back which UUIDs it actually updated
        const { data } = await axiosResource.post(
          `reports/document/photo/${photoType}`,
          { reportUuid, key: null }
        );
        const affectedUuids = data.affectedUuids || [];

        // 2) UI cleanup
        globalSwal.successSimple(this.$t('success.photoDeleted'));
        this.photoSaved = false;
        this.uploadedPhotoKey = null;
        this.previewUrl = '';

        // 3) Reset any local & Vuex state, then close
        this.$store.commit('resetPhotoState');
        this.$bvModal.hide('modal-photo-action');

        // 4) Notify the parent with (newKey, newUrl, affectedUuids, photoType)
        //    Here newKey and newUrl are both null because we just deleted.
        this.$emit('photo-updated', null, null, affectedUuids, photoType);
      } catch (err) {
        console.error('deletePhotoAndRecord error:', err);
        globalSwal.errorSimple(this.$t('error.photoDelete'));
      }
    },
    /**
     * Remove the photo both in S3 and in the DB (and write a history row).
     * Reuses the same bulk‐update endpoint with key = null.
     */
    // async deletePhotoAndRecord() {
    //   const photoType = this.$store.getters['photoType']; // "closure" or "solved"
    //   const reportUuid =
    //     this.report && this.report.uuid
    //       ? this.report.uuid
    //       : this.$store.getters.reportUuid;

    //   try {
    //     // 1) Tell your API to clear the DB field (key=null) and return which UUIDs were updated
    //     const { data } = await axiosResource.post(
    //       `reports/document/photo/${photoType}`,
    //       { reportUuid, key: null }
    //     );
    //     const affectedUuids = data.affectedUuids || [];

    //     // 2) Only delete from S3 if *more than one* report was updated
    //     // NO FOR THE TIME BEING: never delete the image
    //     // (would need to check if the image is not used anywhere else)
    //     if (affectedUuids.length > 1 && this.uploadedPhotoKey) {
    //       // await axiosResource.delete(
    //       //   `/uploads/deleteImage/${this.uploadedPhotoKey}`
    //       // );
    //     }

    //     // 3) UI cleanup
    //     globalSwal.successSimple(this.$t('success.photoDeleted'));
    //     this.photoSaved = false;
    //     this.uploadedPhotoKey = null;
    //     this.previewUrl = '';

    //     // 4) reset store & close
    //     this.$store.commit('resetPhotoState');
    //     this.$bvModal.hide('modal-photo-action');

    //     // 5) notify parent (key=null, url=null, affected UUID list, and photoType)
    //     this.$emit('photo-updated', null, null, affectedUuids, photoType);
    //   } catch (err) {
    //     globalSwal.errorSimple(this.$t('error.photoDelete'));
    //   }
    // },
    async onHidden() {
      // If the modal is closed without saving, delete the uploaded image from S3.
      if (!this.photoSaved && this.uploadedPhotoKey) {
        try {
          await axiosResource.delete(
            `/uploads/deleteImage/${this.uploadedPhotoKey}`
          );
          console.log('Unsaved uploaded image deleted from S3.');
        } catch (error) {
          console.error('Error deleting unsaved photo on modal close:', error);
        }
      }
      // Reset all state.
      this.selectedFile = null;
      this.previewUrl = '';
      this.uploadedPhotoKey = '';
      this.progressBlock = false;
      this.uploadProgress = 0;
      // Use the ref to reset the input value if it exists
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      // Reset the flag
      this.photoSaved = false;
    },
  },
};
</script>

<style scoped>
input[type="file"] {
  display: none;
}

.dropzone {
  width: 100%;
  height: 180px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  background-color: #fff;
  transition: 0.3s ease all;
  display: flex;
  align-items: center;
  justify-content: center;
}
.active-dropzone {
  background-color: rgba(32, 159, 255, 0.06);
  border: 2px dashed #409eff;
}
.preview {
  text-align: center;
}
.preview img {
  max-width: 100%;
  max-height: 180px;
}
.dropzone label {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dropzone i {
  font-size: 3rem;
  color: #c0c4cc;
  margin-bottom: 8px;
}
.dropzone-blue {
  color: #409eff;
}
</style>